import styled from 'styled-components';
import { themeGet } from '@styled-system/theme-get';

import Image from '../../../Core/Image';
import Points from '../../../../images/pts-icon.svg';
import Ctas from '../../../Core/Ctas';

export const Card = styled.li`
  background-color: ${({ theme, $backgroundColor }) =>
    $backgroundColor
      ? theme.colors[$backgroundColor]
      : themeGet('colors.white')};

  position: relative;
  padding: 100px 15px 30px;
  min-height: 260px;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  &.pcma-list-section,
  &.title-with-statement {
    padding: 1.25rem 0.75rem;
  }

  @media only screen and (min-width: ${themeGet('breakpoints.md')}) {
    min-height: 285px;

    &.columns-1 {
      flex-direction: row;
      align-items: center;
      min-height: 225px;
      justify-content: space-between;
      padding: 0 50px;
    }
  }

  @media only screen and (min-width: ${themeGet('breakpoints.xlg')}) {
    &.columns-1 {
      padding: 0 100px;
    }

    &.columns-2 {
      flex-direction: row;
      align-items: center;
      min-height: 225px;
      padding: 2rem;
    }
  }
`;

export const ImageWrapper = styled.div`
  position: absolute;
  width: 100%;
  bottom: 100%;
  transform: translateY(50%);

  @media all and (-ms-high-contrast: none), (-ms-high-contrast: active) {
    /* This fixes IE align-items: center bug */
    left: 0;
    right: 0;
  }

  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;

  @media only screen and (min-width: ${themeGet('breakpoints.md')}) {
    ${Card}.columns-1 & {
      position: relative !important;
      bottom: auto;
      transform: none;
      max-height: 100%;
      min-width: unset;
      max-width: 20%;
    }
  }

  @media only screen and (min-width: ${themeGet('breakpoints.xlg')}) {
    ${Card}.columns-2 & {
      position: relative !important;
      bottom: auto;
      transform: none;
    }
  }
`;

export const Img = styled(Image)`
  height: 100%;
  width: 100%;
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
  object-fit: contain;
  max-height: 168px;

  @media only screen and (min-width: ${themeGet('breakpoints.xlg')}) {
    max-height: 210px;
  }

  ${Card}.columns-2 & {
    max-height: 165px;
    margin: 0;
    max-width: none;
  }
`;

export const Body = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  flex: 1 1 auto;
  width: 100%;

  a {
    margin-top: 0.5rem;
  }

  ${Card}.columns-2 & {
    a {
      margin-top: 0;
    }
  }

  @media only screen and (min-width: ${themeGet('breakpoints.md')}) {
    ${Card}.columns-1 & {
      align-items: center;
      max-width: 60%;
      flex-direction: row;

      p {
        font-size: 1rem;
        line-height: 130%;
      }

      a {
        margin-top: 0;
      }
    }
  }

  @media only screen and (min-width: ${themeGet('breakpoints.xlg')}) {
    margin-top: 0;

    ${Card}.columns-2 & {
      align-self: center;
      align-items: flex-start;
      text-align: left;
    }
  }
`;

export const TextWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  align-items: center;
  margin: 1rem;
  width: 100%;

  h2,
  h3 {
    color: ${({ theme, $backgroundColor }) =>
      $backgroundColor === 'velvet' ? theme.colors.white : null};
    text-align: ${({ $textAlignment }) => $textAlignment || 'left'};
    font-size: 1.375rem;

    @media only screen and (min-width: ${themeGet('breakpoints.md')}) {
      font-size: 1.5rem;
    }

    span {
      font-size: inherit;
      color: inherit;
    }

    span.points {
      font-size: 6rem;
      display: block;
      margin: 1.25rem 0;
      font-weight: 900;
      letter-spacing: -0.5 px;

      @media only screen and (min-width: ${themeGet('breakpoints.md')}) {
        font-size: 4rem;
        margin: 1 rem 0;
      }

      @media only screen and (min-width: ${themeGet('breakpoints.lg')}) {
        font-size: 6rem;
      }

      &:after {
        content: '';
        background-image: url(${Points});
        background-size: contain;
        background-position: center;
        background-repeat: no-repeat;
        position: relative;
        height: 3rem;
        width: 3rem;
        display: inline-block;
        bottom: 2rem;
        right: 0;

        @media only screen and (min-width: ${themeGet('breakpoints.md')}) {
          bottom: 1rem;
        }

        @media only screen and (min-width: ${themeGet('breakpoints.lg')}) {
          bottom: 2rem;
        }
      }
    }
  }

  p {
    color: ${({ theme, $backgroundColor }) =>
      $backgroundColor === 'velvet' ? theme.colors.white : null};
    font-size: 1rem;
    text-align: center;

    @media only screen and (min-width: ${themeGet('breakpoints.md')}) {
      text-align: left;
    }

    @media only screen and (min-width: ${themeGet('breakpoints.lg')}) {
      font-size: 2rem;
    }
  }

  @media only screen and (min-width: ${themeGet('breakpoints.md')}) {
    &.left,
    &.left-aligned {
      align-items: flex-start;

      &.columns-2 {
        align-items: center;
        text-align: center;
      }
    }

    &.center {
      align-items: center;
    }

    &.right,
    &.right-aligned {
      align-items: flex-end;
    }
  }

  @media only screen and (min-width: ${themeGet('breakpoints.xlg')}) {
    &.left,
    &.left-aligned {
      align-items: flex-start;

      &.columns-2 {
        align-items: flex-start;
        text-align: left;
      }
    }
  }

  p:first-child {
    margin-bottom: 0.5rem;
  }

  ${Card}.columns-2 & {
    margin: 0;

    h2,
    h3 {
      margin-bottom: 0.5rem;
      font-size: 1.375rem;
      line-height: 1.875rem;
    }
  }

  ${Card}.columns-1 & {
    @media only screen and (min-width: ${themeGet('breakpoints.md')}) {
      p:first-child {
        margin-bottom: 0;
      }
    }

    @media only screen and (min-width: 1025px) {
      max-width: none;
    }
  }
`;

export const SmallCopy = styled.p`
  font-weight: ${themeGet('fontWeights.normal')};
  font-stretch: normal;
  font-style: normal;
  letter-spacing: normal;
  text-align: center;
  font-size: 14px;
  line-height: 1.36;
  color: ${themeGet('colors.secondary-text')};
  margin: 0.5rem;

  ${Card}.columns-1 & {
    margin: 0.5rem 0;

    @media only screen and (min-width: ${themeGet('breakpoints.md')}) {
      margin: 1rem 0 0.5rem;
    }
  }

  ${Card}.columns-2 & {
    margin: 0 0 1rem;
  }

  @media all and (-ms-high-contrast: none), (-ms-high-contrast: active) {
    /* This fixes IE text overflowing parents */
    width: 100%;
  }

  @media only screen and (min-width: ${themeGet('breakpoints.md')}) {
    font-size: 12px;
    line-height: 1.5;

    ${Card}.columns-1 & {
      text-align: left;
      font-size: 1rem !important;
      line-height: 1.5rem !important;
      margin-top: 1rem;
    }

    ${Card}.columns-2 & {
      font-size: 1.25rem;
    }
  }

  @media only screen and (min-width: ${themeGet('breakpoints.xlg')}) {
    font-size: 14px;

    ${Card}.columns-1 & {
      font-size: 1.125rem !important;
      line-height: 1.75rem !important;
    }

    ${Card}.columns-2 & {
      text-align: left;
    }
  }
`;

export const LegalCopy = styled.p`
  ${Card}.columns-1 &,
  ${Card}.columns-2 & {
    margin: 0.5rem 0;
    font-size: 10px !important;
    line-height: 2.8 !important;
    letter-spacing: normal;
    color: ${themeGet('colors.main-text')};
  }
`;

export const StyledCtas = styled(Ctas)`
  margin-top: 0rem;
  gap: 1.5rem;
`;
